import React from "react";
import {useNavigate} from "react-router-dom";
import {
    createContractAssets,
    removeContractAssets,
    removeContracts,
    updateContract
} from "../../../../../api/asset/AssetContractService";
import Th_title from "../../../../../components/Th_title";

import {contractClassification, contractType} from "../AssetContractCreate";
import {SelectComboBoxForUpdate} from "../../../../../components/select/SelectComboBoxForUpdate";
import {commonResponseMessage} from "../../../../../components/commonmethod/CommonMethod";
import {MemoList} from "../../../../../components/memo/MemoList";
import SimmsDatePicker from "../../../../../components/datepicker/SimmsDatePicker";
import dayjs from "dayjs";
import {cn} from "../../../../../shadCn/lib/utils";
import SimmsInput from "../../../../../components/input/SimmsInput";
import SimmsTextArea from "../../../../../components/input/SimmsTextArea";
import {inputOnChange, inputOnKeyDown} from "../../../../../components/commonmethod/simmsInputMethod";
import {dateOnChange, dateOnSelected} from "../../../../../components/commonmethod/simmsDateMethod";
import {onUpdateClickListener} from "../../../../../components/commonmethod/simmsDrawerMethod";
import Selector from "../../../../../components/selector/Selector";
import SimmsInputSearch from "../../../../../components/input/SimmsInputSearch";
import SimmsDrawerSearch from "../../../../../components/drawer/simms/SimmsDrawerSearch";
import {AssetSearchFilters} from "../../../mng/info/shared/AssetSearchFilters";
import SearchSelector from "../../../../../components/searchSelector/SearchSelector";
import UserSelectionField from "../../../../../components/selection/UserSelectionField";
import {handleActiveError} from "../../../../../components/sonner/ToastBasic";
import {useSelector} from "react-redux";
import {
    ContentDetailTwoColLayout,
    DetailEntityShell,
    DetailFilePanel,
    DetailKvListValue,
    KV_LIST_TYPES,
    DetailKvReadOnly,
    DetailKvTable,
    DetailPanel,
    DetailRollupActions,
    useDetailEditing,
} from "../../../../../components/detailLayout";

function buildContractMeta(contract) {
    return [
        contract.number && `번호 ${contract.number}`,
        contract.company?.name && `업체 ${contract.company.name}`,
    ]
        .filter(Boolean)
        .join(" · ");
}

function AssetContractInfo(props) {
    const navigate = useNavigate();
    if (!props.contract) return null;

    const remove = async () => {
        const response = await removeContracts({ids: [props.contract.id]});
        if (response?.status === 200) {
            alert("계약이 정상적으로 삭제되었습니다.");
            navigate("/asset/contract");
        }
    };

    return (
        <DetailEntityShell
            title={props.contract.name || "계약상세"}
            meta={buildContractMeta(props.contract)}
            canUpdate={props.contract.auth?.update}
            listUrl="/asset/contract"
            scrollMode="columns"
            rollupActions={props.contract.auth?.delete ? <DetailRollupActions remove={remove} /> : null}
        >
            <AssetContractInfoBody {...props} />
        </DetailEntityShell>
    );
}

function AssetContractInfoBody({contract, setContract, assets, setAssets}) {
    const platFrom = useSelector((state) => state.auth.platFrom);
    const isEditing = useDetailEditing();
    const fieldAuth = isEditing && contract.auth?.update;

    const AssetsDeleteHandler = async (id, data) => {
        await removeContractAssets(id, data).then(response => {
            if(response.status === 200) {
                setAssets(prevState => prevState.filter(asset => asset.id !== data.id));
                return commonResponseMessage(response, false, "자산");
            }else if (response.status === 201 || (response.status === 202)) {
                handleActiveError({message: response.data.title, description: response.data.reason});
            }
        });
    }

    const handleAssetsMultiFlat = async (list) => {
        const next = Array.isArray(list) ? list.filter(Boolean) : [];
        const prevIds = new Set((assets ?? []).map((a) => a.id));
        const nextIds = new Set(next.map((a) => a.id));

        for (const item of next) {
            if (!prevIds.has(item.id)) {
                const response = await createContractAssets(contract.id, item);
                if (response.status === 200) {
                    setAssets((prev) => [...prev, item]);
                    commonResponseMessage(response, true, "자산");
                } else if (response.status === 201 || response.status === 202) {
                    handleActiveError({
                        message: response.data.title,
                        description: response.data.reason,
                    });
                }
            }
        }

        for (const item of assets ?? []) {
            if (!nextIds.has(item.id)) {
                await AssetsDeleteHandler(contract.id, item);
            }
        }
    };

    return (
        <ContentDetailTwoColLayout
            main={
            <>
                <DetailPanel title="기본정보">
                    <DetailKvTable>
                        <tbody>
                            <tr>
                                <DetailKvReadOnly label="등록일시" value={contract.createdAt} type="date" />
                                <DetailKvReadOnly label="수정일시" value={contract.updatedAt} type="date" />
                            </tr>
                            <tr>
                                <DetailKvReadOnly label="등록자" value={contract.createdBy} />
                                <DetailKvReadOnly label="수정자" value={contract.updatedBy} />
                            </tr>
                            <tr>
                                <Th_title title={"계약명"} colspan={1}  className="fieldset-td" required={true} auth={fieldAuth}>
                                    <SimmsInput onChange={(v)=>inputOnChange(setContract,"name", v, true)}
                                                update onKeyDown={(v, e)=>inputOnKeyDown(setContract, updateContract, contract.id, "name", v, e, true)}
                                                data={contract.name} />
                                </Th_title>
                                <Th_title title={"계약번호"} colspan={1}  className="fieldset-td" auth={fieldAuth}>
                                    <SimmsInput onChange={(v)=>inputOnChange(setContract,"number", v, false)}
                                                update onKeyDown={(v, e)=>inputOnKeyDown(setContract, updateContract, contract.id, "number", v, e, false)}
                                                data={contract.number} />
                                </Th_title>
                            </tr>
                            <tr>
                                <Th_title title={"요청자"} colspan={1}  className="fieldset-td" required auth={fieldAuth}>
                                    <Selector
                                        onChange={(v)=>onUpdateClickListener(setContract, updateContract, "requester", contract, v, true)}
                                        data={contract.requester}
                                        entity={"userAccount"}
                                        uniqueKey={'requester'}
                                    >
                                        <SimmsInputSearch type={'TEXT'} placeHolder={"사용자명"} />
                                        <SimmsDrawerSearch showLabel title={'부서'} entity={'department'} target={'department'} recursive />
                                        <SimmsDrawerSearch showLabel title={'업체'} entity={'company'} target={'company'} />
                                    </Selector>
                                </Th_title>
                                <Th_title title={"업체명"} colspan={1}  className="fieldset-td" required auth={fieldAuth}>
                                    <Selector
                                        onChange={(v)=>onUpdateClickListener(setContract, updateContract, "company", contract ,v, true)}
                                        data={contract.company}
                                        entity={"company"}
                                        uniqueKey={'company'}
                                    >
                                        <SimmsInputSearch type={'TEXT'} placeHolder={"업체명"} />
                                    </Selector>
                                </Th_title>
                            </tr>
                            <tr>
                                <Th_title title={"계약구분"} colspan={1}  className="fieldset-td" auth={fieldAuth}>
                                    {isEditing ? (
                                        <SelectComboBoxForUpdate target={"type"} id={contract.id} options={contractType} defaultValue={contract.type} listener={updateContract} fnc={setContract} />
                                    ) : (
                                        <span className="asset-detail-kv-value">{contract.type}</span>
                                    )}
                                </Th_title>
                                <Th_title title={"계약종류"} colspan={1}  className="fieldset-td" auth={fieldAuth}>
                                    {isEditing ? (
                                        <SelectComboBoxForUpdate target={"classification"} id={contract.id} options={contractClassification} defaultValue={contract.classification} listener={updateContract} fnc={setContract} />
                                    ) : (
                                        <span className="asset-detail-kv-value">{contract.classification}</span>
                                    )}
                                </Th_title>
                            </tr>
                            <tr>
                                <Th_title title={"계약체결일"} colspan={3}  className="fieldset-td fieldset-td-input" auth={fieldAuth}>
                                    <SimmsDatePicker
                                        auth={fieldAuth}
                                        onChange={(v)=>dateOnChange(setContract,"contractDate", v, false)}
                                        update
                                        onUpdate={(v)=>dateOnSelected(setContract, updateContract, contract.id, "contractDate", v, false)}
                                        data={contract.contractDate}
                                    />
                                </Th_title>
                            </tr>
                            <tr>
                                <Th_title title={"계약기간"} colspan={3} className="fieldset-td fieldset-td-input">
                                    <div className="web-flex asset-detail-date-range">
                                        <SimmsDatePicker
                                            onChange={(v)=>dateOnChange(setContract,"contractStartDate", v, false)}
                                            data={contract.contractStartDate}
                                            update onUpdate={(v)=>dateOnSelected(setContract, updateContract, contract.id, "contractStartDate", v, false)}
                                            constraint={{lessThan : contract.contractEndDate && dayjs(contract.contractEndDate)}} auth={fieldAuth} />
                                        <span className={cn("ml-2 mr-1 text-[20px]")}> ~ </span>
                                        <SimmsDatePicker
                                            onChange={(v)=>dateOnChange(setContract,"contractEndDate", v, false)}
                                            data={contract.contractEndDate}
                                            update onUpdate={(v)=>dateOnSelected(setContract, updateContract, contract.id, "contractEndDate", v, false)}
                                            constraint={{largerThan : contract.contractStartDate && dayjs(contract.contractStartDate)}} auth={fieldAuth} />
                                    </div>
                                </Th_title>
                            </tr>
                            <tr>
                                <Th_title title={"유지보수 요율"} colspan={1}  className="fieldset-td" auth={fieldAuth}>
                                    <SimmsInput onChange={(v)=>inputOnChange(setContract,"rate", v, false)}
                                                update onKeyDown={(v, e)=>inputOnKeyDown(setContract, updateContract, contract.id, "rate", v, e, false)}
                                                data={contract.rate} />
                                </Th_title>
                                <Th_title title={"유지보수 금액(연간)"} colspan={1}  className="fieldset-td" auth={fieldAuth}>
                                    <SimmsInput onChange={(v)=>inputOnChange(setContract,"price", v, false)}
                                                update onKeyDown={(v, e)=>inputOnKeyDown(setContract, updateContract, contract.id, "price", v, e, false)}
                                                data={contract.price} />
                                </Th_title>
                            </tr>
                            <tr>
                                <Th_title title={"계약정보"} colspan={3} className="fieldset-td fieldset-td-input" auth={fieldAuth}>
                                    <SimmsTextArea onChange={(v)=>inputOnChange(setContract,"etc", v, false)}
                                                   update
                                                   style={{width: "100%"}}
                                                   onKeyDown={(v,e)=>inputOnKeyDown(setContract, updateContract, contract.id, "etc", v,e,false)}
                                                   data={contract.etc} />
                                </Th_title>
                            </tr>
                            <tr className={isEditing ? "asset-detail-kv-row--auto" : undefined}>
                                <th>자산목록</th>
                                <td colSpan={3} className="fieldset-td relative align-top">
                                    {isEditing ? (
                                        <UserSelectionField
                                            layout="inline"
                                            data={assets}
                                            countUnit="개"
                                            chipEntity="asset"
                                            listModalTitle="자산목록"
                                            onRemove={(v) => AssetsDeleteHandler(contract.id, v)}
                                        >
                                            <SearchSelector
                                                auth={fieldAuth}
                                                multiSelect
                                                variant="compact"
                                                entity="asset"
                                                uniqueKey="assets"
                                                data={assets}
                                                placeHolder="자산명"
                                                onChange={(v) => {
                                                    if (v?.__multiFlat) {
                                                        handleAssetsMultiFlat(v.__multiFlat);
                                                    }
                                                }}
                                            >
                                                <AssetSearchFilters platFrom={platFrom} />
                                            </SearchSelector>
                                        </UserSelectionField>
                                    ) : (
                                        <DetailKvListValue items={assets} listType={KV_LIST_TYPES.asset} />
                                    )}
                                </td>
                            </tr>
                        </tbody>
                    </DetailKvTable>
                </DetailPanel>

                <MemoList entityId={contract.id} entityName={"AssetContract"} title="메모" />
            </>
            }
            aside={
                <DetailFilePanel
                    isEditing={isEditing}
                    tableInfo={{tableName: "asset_contract", joinKey: contract.id}}
                />
            }
        />
    );
}

export {AssetContractInfo}
